/**
 * safe-redirect: is this caller-supplied "where to go next" a path inside THIS
 * app, and nothing else?
 *
 * Exists because a `?next=` that survives the round trip has to be validated by
 * SHAPE, not by allowlist (QA P2-10, 2026-10-04). `/auth/mfa-enroll` matched
 * `next` against three fixed paths, so the admin layout's enrol bounce from
 * `/admin/billing?paystack_callback=1` (commit 71880585, "the enrol bounce
 * keeps the page") was silently replaced by `/app/dashboard` and the card-saved
 * marker was lost. Widening an allowlist one path at a time is how the next
 * screen gets forgotten, so the rule is now the one that actually matters: a
 * same-origin path, never an off-site or scheme URL.
 *
 * What is refused, and why:
 *   - anything not a string, empty, or over 2048 characters;
 *   - anything not starting with exactly one `/` (`//host` and `/\host` are
 *     protocol-relative to a browser, i.e. off-site);
 *   - any backslash, whitespace or control character (browsers normalise
 *     these into separators, which is how filters are usually bypassed);
 *   - anything that, resolved against a placeholder origin, lands on a
 *     different origin (the final word, whatever the string looked like).
 *
 * Returns the normalised `pathname + search + hash`, or null.
 */

const PLACEHOLDER_ORIGIN = 'https://app.invalid';
const MAX_LENGTH = 2048;

export function safeAppPath(raw: unknown): string | null {
  if (typeof raw !== 'string') return null;
  if (raw.length === 0 || raw.length > MAX_LENGTH) return null;
  if (!raw.startsWith('/')) return null;
  if (raw.startsWith('//')) return null;
  // eslint-disable-next-line no-control-regex
  if (/[\\\s\u0000-\u001f\u007f]/.test(raw)) return null;
  let url: URL;
  try {
    url = new URL(raw, PLACEHOLDER_ORIGIN);
  } catch {
    return null;
  }
  if (url.origin !== PLACEHOLDER_ORIGIN) return null;
  return `${url.pathname}${url.search}${url.hash}`;
}
